import * as Highcharts from "highcharts";
import * as moment from "moment-timezone";
import { AlarmModel, AlarmPeriodLimitModel } from "app/models/alarm.model";
import { ProjectModel, ProjectPeriodModel } from "app/models/project.model";
import { ProjectDeviceLF010Settings } from "app/models/projectDevice.model";
import { darkenHex, hexToRgba } from "./color.util";
import { PROJECT_PERIOD_COLORS } from "./project-period-colors";
import { getProjectPeriodSegments } from "./project-periods.core";

export interface AlarmThresholdSeriesOptions {
    projectDeviceId?: string | null;
    lf010Settings?: ProjectDeviceLF010Settings | null;
}

interface LF010LimitChange {
    changedAt: number;
    previousLimit: number;
    limit: number;
}

interface LF010ThresholdSource {
    currentLimitByDevicePeriod: Map<number, number>;
    historyByDevicePeriod: Map<number, LF010LimitChange[]>;
    projectPeriodToDevicePeriod: Map<string, number>;
}

function getOrderedProjectPeriods(periods: ProjectPeriodModel[]): ProjectPeriodModel[] {
    return [...periods].sort((left, right) => {
        const leftStart = getProjectPeriodSegments(left)[0]?.start ?? Number.POSITIVE_INFINITY;
        const rightStart = getProjectPeriodSegments(right)[0]?.start ?? Number.POSITIVE_INFINITY;
        return leftStart - rightStart;
    });
}

function getAlarmPeriodLimit(alarm: AlarmModel, periodName: string): number {
    const matchingPeriodLimit = alarm.periodLimits?.find((periodLimit) => periodLimit.periodName === periodName);
    return matchingPeriodLimit?.limit ?? alarm.limit;
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === "number" && Number.isFinite(value);
}

function isLF010ManagedAlarm(alarm: AlarmModel, options?: AlarmThresholdSeriesOptions): boolean {
    const alarmProjectDeviceId = alarm.data?.projectDeviceId;

    return (
        alarm.data?.source === "lf010" &&
        !!options?.projectDeviceId &&
        alarmProjectDeviceId != null &&
        String(alarmProjectDeviceId) === String(options.projectDeviceId)
    );
}

function buildLF010ThresholdSource(alarm: AlarmModel, options?: AlarmThresholdSeriesOptions): LF010ThresholdSource | null {
    if (!isLF010ManagedAlarm(alarm, options)) {
        return null;
    }

    const devicePeriods = options?.lf010Settings?.devicePeriods ?? [];
    const periodMappings = options?.lf010Settings?.periodMappings ?? [];
    if (!devicePeriods.length || !periodMappings.length) {
        return null;
    }

    const currentLimitByDevicePeriod = new Map<number, number>();
    for (const devicePeriod of devicePeriods) {
        if (!isFiniteNumber(devicePeriod.devicePeriod) || !isFiniteNumber(devicePeriod.emissionLimit)) {
            return null;
        }

        currentLimitByDevicePeriod.set(devicePeriod.devicePeriod, devicePeriod.emissionLimit);
    }

    const projectPeriodToDevicePeriod = new Map<string, number>();
    for (const mapping of periodMappings) {
        const projectPeriodName = mapping.projectPeriodName?.trim();
        if (!isFiniteNumber(mapping.devicePeriod) || !projectPeriodName || !currentLimitByDevicePeriod.has(mapping.devicePeriod)) {
            return null;
        }

        if (!projectPeriodToDevicePeriod.has(projectPeriodName)) {
            projectPeriodToDevicePeriod.set(projectPeriodName, mapping.devicePeriod);
        }
    }

    if (!projectPeriodToDevicePeriod.size) {
        return null;
    }

    const historyByDevicePeriod = new Map<number, LF010LimitChange[]>();
    for (const entry of options?.lf010Settings?.limitHistory ?? []) {
        if (!currentLimitByDevicePeriod.has(entry.devicePeriod)) {
            continue;
        }

        const changedAt = moment(entry.changedAt).valueOf();
        if (!Number.isFinite(changedAt) || !isFiniteNumber(entry.previousEmissionLimit) || !isFiniteNumber(entry.emissionLimit)) {
            return null;
        }

        const history = historyByDevicePeriod.get(entry.devicePeriod) ?? [];
        history.push({
            changedAt,
            previousLimit: entry.previousEmissionLimit,
            limit: entry.emissionLimit,
        });
        historyByDevicePeriod.set(entry.devicePeriod, history);
    }

    historyByDevicePeriod.forEach((history) => history.sort((left, right) => left.changedAt - right.changedAt));

    return {
        currentLimitByDevicePeriod,
        historyByDevicePeriod,
        projectPeriodToDevicePeriod,
    };
}

function getLF010LimitAt(source: LF010ThresholdSource, periodName: string, timestamp: number): number | undefined {
    const devicePeriod = source.projectPeriodToDevicePeriod.get(periodName);
    if (devicePeriod === undefined) {
        return undefined;
    }

    const currentLimit = source.currentLimitByDevicePeriod.get(devicePeriod);
    if (currentLimit === undefined) {
        return undefined;
    }

    const history = source.historyByDevicePeriod.get(devicePeriod) ?? [];
    if (!history.length) {
        return currentLimit;
    }

    if (timestamp < history[0].changedAt) {
        return history[0].previousLimit;
    }

    for (let index = 0; index < history.length; index++) {
        const change = history[index];
        const nextChange = history[index + 1];

        if (timestamp >= change.changedAt && (!nextChange || timestamp < nextChange.changedAt)) {
            return nextChange ? change.limit : currentLimit;
        }
    }

    return currentLimit;
}

function getLF010ChangeTimestamps(source: LF010ThresholdSource, periodName: string, start: number, end: number): number[] {
    const devicePeriod = source.projectPeriodToDevicePeriod.get(periodName);
    if (devicePeriod === undefined) {
        return [];
    }

    return (source.historyByDevicePeriod.get(devicePeriod) ?? [])
        .map((change) => change.changedAt)
        .filter((changedAt) => changedAt > start && changedAt < end);
}

export function buildProjectPeriodPlotBands(
    project: ProjectModel | null | undefined,
    rangeStart: Date | moment.Moment | null | undefined,
    rangeEnd: Date | moment.Moment | null | undefined,
): Highcharts.XAxisPlotBandsOptions[] {
    if (!project?.periods?.length || !rangeStart || !rangeEnd) {
        return [];
    }

    const timezone = project.timezone || "UTC";
    const start = moment(rangeStart).tz(timezone);
    const end = moment(rangeEnd).tz(timezone);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return [];
    }

    const visibleSegments: Array<{
        periodName: string;
        index: number;
        start: number;
        end: number;
        baseColor: string;
    }> = [];
    const orderedPeriods = getOrderedProjectPeriods(project.periods);

    for (let day = start.clone().startOf("day"); day.isBefore(end); day = day.clone().add(1, "day")) {
        orderedPeriods.forEach((period, index) => {
            const baseColor = PROJECT_PERIOD_COLORS[index % PROJECT_PERIOD_COLORS.length];
            getProjectPeriodSegments(period).forEach((segment) => {
                const segmentStart = day.clone().add(segment.start, "minutes");
                const segmentEnd = day.clone().add(segment.end, "minutes");
                const clippedStart = moment.max(segmentStart, start);
                const clippedEnd = moment.min(segmentEnd, end);

                if (!clippedEnd.isAfter(clippedStart)) {
                    return;
                }

                visibleSegments.push({
                    periodName: period.name,
                    index,
                    start: clippedStart.valueOf(),
                    end: clippedEnd.valueOf(),
                    baseColor,
                });
            });
        });
    }

    const mergedSegments: typeof visibleSegments = [];

    visibleSegments
        .sort((left, right) => left.start - right.start || left.end - right.end)
        .forEach((segment) => {
            const previous = mergedSegments[mergedSegments.length - 1];
            if (previous && previous.periodName === segment.periodName && previous.end === segment.start) {
                previous.end = segment.end;
                return;
            }

            mergedSegments.push({ ...segment });
        });

    return mergedSegments.map((segment) => ({
        id: `project-period-band-${segment.index}-${segment.periodName}-${segment.start}-${segment.end}`,
        from: segment.start,
        to: segment.end,
        color: hexToRgba(segment.baseColor, 0.22),
        zIndex: 1,
        label: {
            text: `<span style="display:inline-block;padding:2px 16px;border-radius:999px;background:${hexToRgba(
                darkenHex(segment.baseColor, 0.88),
                0.96,
            )};">${segment.periodName}</span>`,
            useHTML: true,
            style: {
                color: "rgba(15, 23, 42, 0.84)",
                fontSize: "11px",
                fontWeight: "600",
                textOutline: "none",
            },
        },
    }));
}

export function buildAlarmThresholdSeries(
    alarm: AlarmModel | undefined,
    project: ProjectModel | null | undefined,
    rangeStart: Date | moment.Moment | null | undefined,
    rangeEnd: Date | moment.Moment | null | undefined,
    options?: AlarmThresholdSeriesOptions,
): Array<[number, number]> {
    if (!alarm || !rangeStart || !rangeEnd) {
        return [];
    }

    const start = moment(rangeStart);
    const end = moment(rangeEnd);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
        return [];
    }

    if (!project?.periods?.length || !alarm.periodLimits?.length) {
        return [
            [start.valueOf(), alarm.limit],
            [end.valueOf(), alarm.limit],
        ];
    }

    const timezone = project.timezone || "UTC";
    const localStart = start.clone().tz(timezone);
    const localEnd = end.clone().tz(timezone);
    const thresholdSegments: Array<{ start: number; end: number; limit: number }> = [];
    const orderedPeriods = getOrderedProjectPeriods(project.periods).map((period) => ({
        period,
        segments: getProjectPeriodSegments(period),
        alarmLimit: getAlarmPeriodLimit(alarm, period.name),
    }));
    const lf010ThresholdSource = buildLF010ThresholdSource(alarm, options);

    for (let day = localStart.clone().startOf("day"); day.isBefore(localEnd); day = day.clone().add(1, "day")) {
        orderedPeriods.forEach(({ period, segments, alarmLimit }) => {
            segments.forEach((segment) => {
                const segmentStart = day.clone().add(segment.start, "minutes");
                const segmentEnd = day.clone().add(segment.end, "minutes");
                const clippedStart = moment.max(segmentStart, localStart);
                const clippedEnd = moment.min(segmentEnd, localEnd);

                if (!clippedEnd.isAfter(clippedStart)) {
                    return;
                }

                const clippedStartValue = clippedStart.valueOf();
                const clippedEndValue = clippedEnd.valueOf();
                const splitTimestamps = [
                    clippedStartValue,
                    ...(lf010ThresholdSource
                        ? getLF010ChangeTimestamps(lf010ThresholdSource, period.name, clippedStartValue, clippedEndValue)
                        : []),
                    clippedEndValue,
                ].sort((left, right) => left - right);

                for (let index = 0; index < splitTimestamps.length - 1; index++) {
                    const segmentStartValue = splitTimestamps[index];
                    const segmentEndValue = splitTimestamps[index + 1];
                    const limit = lf010ThresholdSource
                        ? (getLF010LimitAt(lf010ThresholdSource, period.name, segmentStartValue) ?? alarmLimit)
                        : alarmLimit;

                    thresholdSegments.push({
                        start: segmentStartValue,
                        end: segmentEndValue,
                        limit,
                    });
                }
            });
        });
    }

    if (!thresholdSegments.length) {
        return [
            [start.valueOf(), alarm.limit],
            [end.valueOf(), alarm.limit],
        ];
    }

    const orderedSegments = thresholdSegments
        .filter((segment) => segment.end > segment.start)
        .sort((left, right) => left.start - right.start);
    const mergedSegments: Array<{ start: number; end: number; limit: number }> = [];

    for (const segment of orderedSegments) {
        const previous = mergedSegments[mergedSegments.length - 1];
        if (previous && previous.end === segment.start && previous.limit === segment.limit) {
            previous.end = segment.end;
            continue;
        }

        mergedSegments.push({ ...segment });
    }

    const points: Array<[number, number]> = [];
    mergedSegments.forEach((segment, index) => {
        points.push([segment.start, segment.limit]);

        if (index === mergedSegments.length - 1) {
            points.push([segment.end, segment.limit]);
        }
    });

    return points;
}

export function describeAlarmLimits(alarm: AlarmModel, project?: ProjectModel | null): string {
    if (!alarm.periodLimits?.length || !project?.periods?.length) {
        return `${alarm.limit}`;
    }

    const limitsByPeriod = new Map<string, AlarmPeriodLimitModel[]>();
    alarm.periodLimits.forEach((periodLimit) => {
        const existingLimits = limitsByPeriod.get(periodLimit.periodName) ?? [];
        existingLimits.push(periodLimit);
        limitsByPeriod.set(periodLimit.periodName, existingLimits);
    });

    return project.periods
        .map((period) => {
            const namedLimits = limitsByPeriod.get(period.name) ?? [];
            const fallbackLimit = namedLimits[0]?.limit ?? alarm.limit;

            return `${period.name}: ${fallbackLimit}`;
        })
        .join(" | ");
}
